
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from '@/lib/utils';
import { Edit, Eye, Trash2 } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ActionMenu } from "@/components/ui/action-menu";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";
import { deleteGiftRecord } from "@/services/gift/enhancedGiftService";

interface Gift {
  id: string;
  name: string;
  date_sent: string | null;
  status: string | null;
  value: number | null;
  shipping_status: string | null;
  contact_id: string | null;
  contacts: { first_name: string; last_name: string } | null;
  organization_id: string | null;
  organizations: { name: string } | null;
}

interface GiftListProps {
  gifts: Gift[];
  isLoading: boolean;
}

const GiftStatusBadge = ({ status }: { status: string | null }) => {
  if (!status) return null;

  const statusMap: Record<string, { color: string, label: string }> = {
    'planned': { color: 'default', label: 'Planned' },
    'purchased': { color: 'secondary', label: 'Purchased' },
    'shipped': { color: 'blue', label: 'Shipped' },
    'delivered': { color: 'green', label: 'Delivered' },
    'cancelled': { color: 'destructive', label: 'Cancelled' },
  };

  const statusConfig = statusMap[status] || { color: 'default', label: status };

  return (
    <Badge variant={statusConfig.color as any}>{statusConfig.label}</Badge>
  );
};

const GiftList: React.FC<GiftListProps> = ({ gifts, isLoading }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [giftToDelete, setGiftToDelete] = useState<Gift | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDelete = async () => {
    if (!giftToDelete) return;
    
    try {
      await deleteGiftRecord(giftToDelete.id);
      queryClient.invalidateQueries({ queryKey: ['gifts'] });
      toast.success('Gift deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete gift: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-4 p-2">
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  if (gifts.length === 0) {
    return (
      <div className="text-center py-10">
        <h3 className="text-lg font-medium">No gifts yet</h3>
        <p className="text-muted-foreground">Create your first gift to get started.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Gift</TableHead>
            <TableHead>Recipient</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Value</TableHead>
            <TableHead className="w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {gifts.map((gift) => {
            const recipientName = gift.contacts 
              ? `${gift.contacts.first_name} ${gift.contacts.last_name}` 
              : gift.organizations?.name || 'N/A';

            const actionItems = [
              {
                label: "View",
                icon: <Eye className="mr-2 h-4 w-4" />,
                href: `/gifts/${gift.id}`
              },
              {
                label: "Edit",
                icon: <Edit className="mr-2 h-4 w-4" />,
                href: `/gifts/${gift.id}/edit`
              },
              {
                label: "Delete",
                icon: <Trash2 className="mr-2 h-4 w-4" />,
                variant: "destructive" as const,
                onClick: () => {
                  setGiftToDelete(gift);
                  setDeleteDialogOpen(true);
                }
              }
            ];

            return (
              <TableRow key={gift.id}>
                <TableCell className="font-medium">{gift.name}</TableCell>
                <TableCell>{recipientName}</TableCell>
                <TableCell>{gift.date_sent ? new Date(gift.date_sent).toLocaleDateString() : 'Not sent'}</TableCell>
                <TableCell>
                  <GiftStatusBadge status={gift.status} />
                </TableCell>
                <TableCell>{gift.value ? formatCurrency(gift.value) : '-'}</TableCell>
                <TableCell>
                  <ActionMenu items={actionItems} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <DeleteConfirmation
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setGiftToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Gift"
        description={giftToDelete ? `Are you sure you want to delete "${giftToDelete.name}"? This action cannot be undone.` : ""}
        entityName="gift"
      />
    </div>
  );
};

export default GiftList;
